import HelpButton from "@/components/HelpButton/HelpButton";
import NoImpactBanner from "@/components/NoImpactBanner/NoImpactBanner";
import { PageHeader } from "@/components/PageHeader/PageHeader";
import { useCompanyAccess } from "@/hooks/useCompanySettings";
import styles from "@/pages/dashboard/Dashboard.module.scss";
import { Button } from "antd";
import { Link, useNavigate } from "react-router-dom";
import MetricSelector from "../components/MetricSelector/MetricSelector";
import YearPicker from "../components/YearPicker/YearPicker";
import { useDashboardYear } from "../hooks/useDateRange";
import useImpactMetric from "../hooks/useImpactMetric";
import { useDashboardOrderCounts } from "../hooks/usePoSo";
import PackagingImpactPerMaterial from "./widgets/PackagingImpactPerMaterial";
import PackagingImpactPerScope from "./widgets/PackagingImpactPerScope";
import PackagingImpactTopContributors from "./widgets/PackagingImpactTopContributors";
import PackagingImpactTotal from "./widgets/PackagingImpactTotal";
import PremiumExplore from "@/components/PremiumFeatureComponents/PremiumExplore";
import { usePackagingImpactTotals } from "@/pages/dashboard/hooks/usePackagingImpact";
import PackagingImpactPerType from "@/pages/dashboard/PackagingDashboard/widgets/PackagingImpactPerType";

export default function PackagingDashboard() {
  const metric = useImpactMetric();
  const navigate = useNavigate();
  const access = useCompanyAccess();

  const { data: orderCounts } = useDashboardOrderCounts({});
  const { data: totalImpact } = usePackagingImpactTotals({ metrics: [metric.key] });
  const { year } = useDashboardYear();
  const pageTitle = `Packaging impact`;
  const pageDescription =
    "Insight into the part of your total company impact attributed to packaging.";

  if (access && !access.includes("lca"))
    return (
      <PremiumExplore
        title={pageTitle}
        description={pageDescription}
      />
    );

  return (
    <div>
      <PageHeader
        title={pageTitle}
        description={pageDescription}
        right={
          <>
            <YearPicker />
            <HelpButton url="#" />
          </>
        }
      />
      <MetricSelector
        value={metric.key}
        onChange={(key) => navigate("/dashboard/packaging/" + key)}
      />
      {orderCounts?.po === 0 ? (
        <NoImpactBanner title={`No packaging impact for ${year}`}>
          <span>
            Start with uploading purchase orders for {year}, so we can calculate your packaging
            impact.
          </span>
          <Link to="/orders/purchase_orders">
            <Button type="primary">Upload orders</Button>
          </Link>
        </NoImpactBanner>
      ) : totalImpact?.packaging_impact[metric.key] === 0 ? (
        <NoImpactBanner title={`No packaging impact for ${year}`}>
          <span>What to do?</span>
          <ul>
            <li>
              Add a product packaging ID to each product in the{" "}
              <Link to="/products">product detail</Link> upload.
            </li>
            <li>
              Upload the <Link to="/packaging">packaging BOMs</Link> linked to those product
              packaging IDs.
            </li>
            <li>
              Upload logistics packaging for each facility in the{" "}
              <Link to="/facilities">facilities</Link> chapter.
            </li>
            <li>
              Match the <Link to="/matching/packaging/materials"> packaging materials</Link> with
              the correct impact reference.
            </li>
          </ul>
        </NoImpactBanner>
      ) : (
        <div className={styles.grid}>
          <div className={styles["two-columns"]}>
            <div>
              <PackagingImpactTotal metric={metric} />
              {metric.key === "carbon_footprint" && <PackagingImpactPerScope />}
              <PackagingImpactPerType metric={metric} />
            </div>
            <div>
              <PackagingImpactTopContributors metric={metric} />
            </div>
          </div>
          <div className={styles["one-column"]}>
            <PackagingImpactPerMaterial metric={metric} />
          </div>
        </div>
      )}
    </div>
  );
}
